import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { CreateRelationResponse } from '../_models/relation.models';
import {
  CreateFormPayload,
  Form,
  FormDataField,
  FormField,
  FormID,
  TabData,
  UpdateFormPayload,
  DeleteObject,
  PostDataBody,
  PutDataBody,
  DependentRecords,
  DependentRecordsSuccess,
  UserDropDown,
  CopyFormPayload,
  CopyFormResponse,
  SavedFields,
  SigningRequestResponse,
} from '../_models/form.model';
import { Dropdown } from '../_models/ignatius.models';
import { Dictionary } from '@ngrx/entity';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private baseURL = `${environment.apiUrl}/api`;
  private midURL = '/form';
  private midActionURL = '/formaction';
  private FORM_BY_TABLE = '/getbytableid?Id=';
  private FORMS_BY_TABLES = '/getformsbytable?';
  private FORM_FIELDS = '/getformfields?id=';
  private GET_FORM = '/loadformtoedit?id=';
  private FORM_FOR_REPORT = '/getformforreport?reportId=';
  private FORM_SAVED_FIELDS = '/getformsavedfields?id=';
  private FORM_DATA = '/loadDataToEdit?formId=';
  private POST_DATA = '/postdata';
  private DELETE_DATA = '/deleteobject';
  private PUT_DATA = '/putdata';
  private SYNC_DATA = '/syncdata';
  private DEPENDENT_RECORDS = '/getdependantrecords';
  private DOWNLOAD_FILE = '/downloadfile';
  private SIGNING_REQUEST = '/getsigningrequest';
  private SIGNING_DOCUMENT_DETAIL = '/getsigningdocumentdetail';
  private SEND_DOCUMENT_CONFIRMATION = '/sentforsigningconfirmation';
  private FORM_FIELD_USERS = '/formfieldusers';
  private GET_REALTED_DATA = '/getrelateddata';
  private COPY = '/copy';
  private GET_FORM_RULES = '/getformrules';

  public get getFormByTable() {
    return `${this.baseURL}${this.midURL}${this.FORM_BY_TABLE}`;
  }

  public get getFormsByTablesUrl() {
    return `${this.baseURL}${this.midURL}${this.FORMS_BY_TABLES}`;
  }

  public get getFormUrl() {
    return `${this.baseURL}${this.midURL}`;
  }

  public get getFormFieldsUrl() {
    return `${this.baseURL}${this.midURL}${this.FORM_FIELDS}`;
  }

  public get getFormLoadUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_FORM}`;
  }

  public get getFormForReportUrl() {
    return `${this.baseURL}${this.midActionURL}${this.FORM_FOR_REPORT}`;
  }

  public get getFormSavedFieldsUrl() {
    return `${this.baseURL}${this.midURL}${this.FORM_SAVED_FIELDS}`;
  }

  public get getFormDataUrl() {
    return `${this.baseURL}${this.midURL}${this.FORM_DATA}`;
  }

  public get getPostDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.POST_DATA}`;
  }

  public get getPutDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.PUT_DATA}`;
  }

  public get getSyncDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.SYNC_DATA}`;
  }

  public get getDeleteDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.DELETE_DATA}`;
  }

  public get getDependentRecordsUrl() {
    return `${this.baseURL}${this.midActionURL}${this.DEPENDENT_RECORDS}`;
  }

  public get getDownloadFileUrl() {
    return `${this.baseURL}${this.midActionURL}${this.DOWNLOAD_FILE}`;
  }

  public get getSigningRequestDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.SIGNING_REQUEST}`;
  }

  public get getSigningDocumentDetailUrl() {
    return `${this.baseURL}${this.midActionURL}${this.SIGNING_DOCUMENT_DETAIL}`;
  }

  public get sendDocumentConfirmationUrl() {
    return `${this.baseURL}${this.midActionURL}${this.SEND_DOCUMENT_CONFIRMATION}`;
  }

  public get getFormFieldUsersUrl() {
    return `${this.baseURL}${this.midURL}${this.FORM_FIELD_USERS}`;
  }

  public get getRelatedDataUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_REALTED_DATA}`;
  }

  public get getCopyFormUrl() {
    return `${this.baseURL}${this.midURL}${this.COPY}`;
  }
  public get getCopyDataUrl() {
    return `${this.baseURL}${this.midActionURL}${this.COPY}`;
  }

  public get getFormRulesUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_FORM_RULES}`;
  }

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Get forms
   */
  getForms = (
    tableId: string,
    inReport: boolean = false
  ): Observable<Array<Form>> =>
    this.http.get<Array<Form>>(
      `${this.getFormByTable}${tableId}&inReport=${inReport}`,
      {
        headers: this.authService.getHeaders(),
      }
    );


    /**
   * Get Forms By Tables
   */
  getFormsByTables = (
    tableIds: Array<string>,
  ): Observable<Dictionary<Array<Form>>> => {
    const params = tableIds.map(id => `tableIds=${encodeURIComponent(id)}`).join('&');
    return this.http.get<Dictionary<Array<Form>>>(
      `${this.getFormsByTablesUrl}${params ? params : ''}`,
      {
        headers: this.authService.getHeaders(),
      }
    );
  }

  /**
   * Get form fields
   */
  getFormfields = (tableId: string): Observable<Array<FormField>> =>
    this.http.get<Array<FormField>>(`${this.getFormFieldsUrl}${tableId}`, {
      headers: this.authService.getHeaders(),
    });

  /**
   * create form
   */
  createform = (
    payload: CreateFormPayload
  ): Observable<CreateRelationResponse> =>
    this.http.post<CreateRelationResponse>(this.getFormUrl, payload, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Get form
   */
  getForm = (tableId: string, formId: string): Observable<Form> =>
    this.http.get<Form>(`${this.getFormLoadUrl}${formId}&tableId=${tableId}`, {
      headers: this.authService.getHeaders(),
    });

  /**
   * update form
   */
  updateform = (payload: UpdateFormPayload): Observable<null> =>
    this.http.put<null>(this.getFormUrl, payload, {
      headers: this.authService.getHeaders(),
    });

  /**
   * delete form
   */
  deleteForm = (formId: string): Observable<void> =>
    this.http.delete<void>(`${this.getFormUrl}?Id=${formId}`, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Get form id
   */
  getFormID = (
    tableId: string,
    reportId: string,
    isPDF: boolean,
    isWord: boolean = false
  ): Observable<FormID> =>
    this.http.get<FormID>(
      `${this.getFormForReportUrl}${reportId}&tableId=${tableId}${
        isPDF ? '&isPdf=true' : isWord ? '&isWord=true' : ''
      }`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * Get form saved field
   */
  getFormSavedFields = (
    tableId: string,
    formId: string,
    defualtForm: boolean = true,
    fromEmbeddedOrDrilldown: boolean = false,
    parentTableId: string = '0',
    reportId: string | null = null
  ): Observable<SavedFields> =>
    this.http.get<SavedFields>(
      `${this.getFormSavedFieldsUrl}${formId}&tableId=${tableId}&viewRecord=true&defaultForm=${defualtForm}&fromEmbeddedOrDrilldown=${fromEmbeddedOrDrilldown}&parentTableId=${parentTableId}&reportId=${reportId}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

    /**
   * Get form rules
   */
  getFormRules = (
    id: string,
    tableId: string
  ): Observable<any> =>
    this.http.get<any>(
      `${this.getFormRulesUrl}?id=${id}&tableId=${tableId}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * Get form data
   */
  getFormData = (
    tableId: string,
    formId: string,
    id: number,
    forProcess: boolean = false,
    forRecordView: boolean = false,
  ): Observable<Array<FormDataField>> =>
    this.http.get<Array<FormDataField>>(
      `${this.getFormDataUrl}${formId}&id=${id}&tableId=${tableId}&forProcess=${forProcess}&forRecordView=${forRecordView}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * Form actions
   */
  /**
   * Post data
   * @param payload PostDataBody
   */
  postData = (payload: PostDataBody): Observable<Array<any>> =>
    this.http.post<Array<any>>(this.getPostDataUrl, payload, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Delete object
   * @param payload info for data to be deleted
   */
  deleteObject = (payload: DeleteObject): Observable<null> => {
    const options = {
      headers: this.authService.getHeaders(),
      body: payload,
    };
    return this.http.delete<null>(this.getDeleteDataUrl, options);
  };

  /**
   * Put data
   * @param payload PutDataBody
   */
  putData = (payload: PutDataBody): Observable<Array<any>> =>
    this.http.put<Array<any>>(this.getPutDataUrl, payload, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Put data
   * @param payload PutDataBody
   */
  syncData = (payload: any): Observable<Array<any>> =>
    this.http.post<Array<any>>(this.getSyncDataUrl, payload, {
      headers: this.authService.getHeaders(),
    });

  /**
   * Get dependent records
   */
  dependentRecords = (
    payload: DependentRecords
  ): Observable<DependentRecordsSuccess> =>
    this.http.post<DependentRecordsSuccess>(
      this.getDependentRecordsUrl,
      payload,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * download
   */
  downloadFile = (
    tableId: string,
    recordId: number,
    fileName: string,
    fieldId: string
  ): Observable<Blob> =>
    this.http.get<Blob>(
      `${this.getDownloadFileUrl}?recordId=${recordId}&tableId=${tableId}&fileName=${fileName}&fieldId=${fieldId}`,
      {
        observe: 'body',
        responseType: 'blob' as 'json',
      }
    );

  /**
   * Get Document Signing Request Data
   */
  getSigningRequestData = (
    tableId: string,
    recordId: number,
    fieldId: string
  ): Observable<SigningRequestResponse> =>
    this.http.get<SigningRequestResponse>(
      `${this.getSigningRequestDataUrl}?recordId=${recordId}&tableId=${tableId}&fieldId=${fieldId}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * Get Document Signing Request Data
   */
  getSigningDocumentDetail = (
    tableId: string,
    recordId: number,
    fieldId: string
  ): Observable<any> =>
    this.http.get<any>(
      `${this.getSigningDocumentDetailUrl}?recordId=${recordId}&tableId=${tableId}&fieldId=${fieldId}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  /**
   * Send Document Confirmation
   */
  sendDocumentConfirmation = (documentId: any): Observable<any> =>
    this.http.post<any>(`${this.sendDocumentConfirmationUrl}?documentId=${documentId}`,{
      headers: this.authService.getHeaders(),
    });

  getFormFieldUsers = (
    tableId: string,
    applicationId: string,
    isSearch: boolean,
    search: string,
    isScroll: boolean,
    skip: number,
    pageSize: number,
    isConditionComponent: boolean,
    fromSMSNotification: boolean = false
  ): Observable<Array<UserDropDown>> =>
    this.http.get<Array<UserDropDown>>(
      `${this.getFormFieldUsersUrl}?tableId=${tableId}&applicationId=${applicationId}&isSearch=${isSearch}&search=${search}&isScroll=${isScroll}&skip=${skip}&pageSize=${pageSize}&isConditionComponent=${isConditionComponent}&fromSMSNotification=${fromSMSNotification}`,
      {
        headers: this.authService.getHeaders(),
      }
    );

  getRelatedData = (
    tableId: string,
    fieldId: string,
    dropdownLabel: string,
    isSearch: boolean,
    search: string,
    isScroll: boolean,
    skip: number,
    pageSize: number,
    isConditionComponent: boolean,
    relatedValue:number = 0,
    relatedFieldName:string='',
    relatedConditions:any,
    selectedValue:any=null
  ): Observable<Array<Dropdown>> =>{
    let payload = {
         TableId:tableId,
         FieldId:fieldId,
         DropdownLabel:dropdownLabel,
         IsSearch:isSearch,
         Search:search,
         IsScroll:isScroll,
         Skip:skip,
         PageSize:pageSize,
         IsConditionComponent:isConditionComponent,
         RelatedValue:relatedValue,
         RelatedFieldName:relatedFieldName,
         RelatedCondition:relatedConditions || null,
         selectedValue
    }
    return this.http.post<Array<Dropdown>>(
      `${this.getRelatedDataUrl}`,payload,
      {
        headers: this.authService.getHeaders(),
      }
    );
  }

  /**
   * download
   */
  downloadFilledPDF = (formId: string, recordId: number): Observable<Blob> =>
    this.http.get<Blob>(
      `${this.getFormUrl}/pdf?formId=${formId}&rid=${recordId}`,
      {
        observe: 'body',
        // responseType: 'blob' as 'json',
      }
    );

  downloadFilledWord = (formId: string, recordId: number): Observable<Blob> =>
    this.http.get<Blob>(
      `${this.getFormUrl}/word?formId=${formId}&rid=${recordId}`,
      {
        observe: 'body',
        // responseType: 'blob' as 'json',
      }
    );

  /**
   * Copy Form
   */
  copyForm = (payload: CopyFormPayload): Observable<CopyFormResponse> =>
    this.http.post<CopyFormResponse>(`${this.getCopyFormUrl}`, payload);
  /**
   * Copy Record
   */
  copyData = (tableId: string,recordId:string,payload:any): Observable<any> =>
    this.http.post<any>(`${this.getCopyDataUrl}?recordId=${recordId}`,payload);

  getSVGData(payload:any) {
    return forkJoin(payload.map((data:any)=>{
      return this.http.get(data,{responseType:'text'});
    }))
  } 

  selectTemplate = (payload: any): Observable<any> => {
    return this.http.post<any>(`${environment.apiUrl}/api/formaction/posttemplate`, payload, {});
  };
}
